import React, { useEffect, useState, useCallback } from 'react';
import { t } from '@/locales/strings';
import { useModalA11y } from '@/hooks/useModalA11y';
import { track } from '@/utils/analytics';
import { entriesForUser, latestWhatsNewIdForUser } from '@/data/whatsNew';
import { whatsNewSeenId, markWhatsNewSeen } from '@/utils/introState';

/**
 * hasUnreadWhatsNew — used by the home header to decide whether to
 * render the banner. Compares the latest entry id (filtered by the
 * user's signup date so brand-new accounts don't see entries about
 * features they never experienced any other way) to the last id
 * the user has acknowledged.
 */
export function hasUnreadWhatsNew(userCreatedAt) {
  const latest = latestWhatsNewIdForUser(userCreatedAt);
  if (!latest) return false;
  return whatsNewSeenId() !== latest;
}

/**
 * WhatsNewBanner — a thin tappable strip rendered just under the
 * home header when there is an unread entry. Tapping opens the
 * full sheet; the close button on the sheet writes the latest id
 * to localStorage so the banner disappears for that user.
 */
export function WhatsNewBanner({ lang = 'en', userCreatedAt, onOpen }) {
  const entries = entriesForUser(userCreatedAt);
  const entry = entries[0];
  if (!entry) return null;
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        margin: 0,
        padding: '10px 16px',
        background:
          'linear-gradient(180deg, var(--primary-pale, #fbeae7) 0%, #fff 130%)',
        border: 'none',
        borderBottom: '1px solid color-mix(in srgb, var(--primary, #c97a68) 16%, transparent)',
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
      }}
    >
      <span style={{
        flexShrink: 0,
        font: '700 9px/1 var(--font-ui, Manrope)',
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        background: 'var(--primary, #c97a68)',
        color: '#fff',
        padding: '4px 8px',
        borderRadius: 999,
      }}>
        {t('whats_new_pill', lang)}
      </span>
      <span style={{
        flex: 1,
        minWidth: 0,
        font: '500 13px/1.3 var(--font-body, Manrope)',
        color: 'var(--ink, #2d1f26)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {t(entry.title, lang)}
      </span>
      <span aria-hidden="true" style={{
        flexShrink: 0,
        color: 'color-mix(in srgb, var(--primary, #c97a68) 70%, transparent)',
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 6l6 6-6 6" />
        </svg>
      </span>
    </button>
  );
}

/**
 * WhatsNewSheet — bottom sheet listing every WHATS_NEW entry. The
 * latest unread one carries the "New" badge. Marks seen on open
 * (not on close) so a force-quit between read and dismiss does not
 * leave the banner re-armed.
 */
export default function WhatsNewSheet({ open, onClose, lang = 'en', userCreatedAt }) {
  const dialogRef = useModalA11y({ open, onClose });
  // Capture the seen-id at the moment we open so the badge on the
  // currently-unread entry stays visible during this view (it would
  // disappear immediately if we read state after the open-write).
  const [seenIdAtOpen, setSeenIdAtOpen] = useState('');
  const visibleEntries = entriesForUser(userCreatedAt);

  useEffect(() => {
    if (open) {
      setSeenIdAtOpen(whatsNewSeenId());
      const latest = latestWhatsNewIdForUser(userCreatedAt);
      if (latest) markWhatsNewSeen(latest);
      track('whats_new_opened');
    }
  }, [open, userCreatedAt]);

  const close = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!open) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={t('whats_new_title', lang)}
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 700,
        background: 'rgba(28, 14, 22, 0.58)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: 'bb-guide-fade 240ms ease-out',
      }}
    >
      <div style={{
        width: '100%',
        maxWidth: 520,
        maxHeight: '88dvh',
        background:
          'linear-gradient(180deg, #fff 0%, var(--primary-pale, #fbeae7) 140%)',
        borderTopLeftRadius: 26,
        borderTopRightRadius: 26,
        paddingTop: 18,
        paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
        paddingLeft: 20,
        paddingRight: 20,
        boxShadow: '0 -30px 80px -20px rgba(45,31,38,.45)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        animation: 'bb-guide-slide 320ms cubic-bezier(0.32,0.72,0,1)',
      }}>
        <div aria-hidden="true" style={{
          width: 44, height: 4, borderRadius: 999,
          background: 'color-mix(in srgb, var(--ink, #2d1f26) 14%, transparent)',
          margin: '0 auto 14px',
        }} />

        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{
            font: '500 22px/1.2 var(--font-title, Fraunces), serif',
            color: 'var(--ink, #2d1f26)',
          }}>
            {t('whats_new_title', lang)}
          </div>
          <div style={{
            font: '400 13px/1.45 var(--font-body, Manrope)',
            color: 'var(--ink-dim, #6b5560)',
            maxWidth: 320,
            margin: '6px auto 0',
            textWrap: 'balance',
          }}>
            {t('whats_new_subtitle', lang)}
          </div>
        </div>

        <div style={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          paddingBottom: 12,
        }}>
          {visibleEntries.map((entry) => {
            const isUnread = entry.id !== seenIdAtOpen;
            return (
              <article
                key={entry.id}
                style={{
                  padding: '14px',
                  borderRadius: 18,
                  background: '#fff',
                  border: '1px solid color-mix(in srgb, var(--primary, #c97a68) 14%, transparent)',
                  boxShadow: '0 6px 18px -14px rgba(45,31,38,0.35)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  {isUnread && (
                    <span style={{
                      font: '700 9px/1 var(--font-ui, Manrope)',
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      background: 'var(--primary, #c97a68)',
                      color: '#fff',
                      padding: '4px 8px',
                      borderRadius: 999,
                    }}>
                      {t('whats_new_pill', lang)}
                    </span>
                  )}
                  <h3 style={{
                    margin: 0,
                    font: '500 16px/1.25 var(--font-title, Fraunces), serif',
                    color: 'var(--ink, #2d1f26)',
                  }}>
                    {t(entry.title, lang)}
                  </h3>
                </div>
                <p style={{
                  margin: 0,
                  font: '400 14px/1.5 var(--font-body, Manrope)',
                  color: 'var(--ink-dim, #6b5560)',
                  textWrap: 'balance',
                }}>
                  {t(entry.body, lang)}
                </p>
              </article>
            );
          })}
        </div>

        <button
          type="button"
          onClick={close}
          style={{
            marginTop: 4,
            width: '100%',
            minHeight: 46,
            borderRadius: 999,
            border: '1px solid color-mix(in srgb, var(--ink, #2d1f26) 10%, transparent)',
            background: 'transparent',
            color: 'var(--ink-dim, #6b5560)',
            font: '600 13px/1 var(--font-ui, Manrope)',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {t('whats_new_close', lang)}
        </button>
      </div>
    </div>
  );
}
